import type { History } from "history";
import { startCase } from "lodash";
import moment from "moment";

import { type Notification, NotificationKey } from "src/types/notification";

import { ThirdServiceCategory } from "src/types/posCredential";

import { useTranslation } from "src/design-system/core/TranslationContext";
import { NOTIFICATIONS_TRANSLATION_NAMESPACE } from "./config";

import { AgendaListTabsEnum, agendaRoutes } from "src/views/agenda/routes";
import { OffersTablesTabsEnum, offersRoutes } from "src/views/offer/routes";
import { OrganizationToolsTabsEnum, organizationRoutes } from "src/views/organization/routes";
import { posRoutes } from "src/views/pos/routes";
import { SponsoredAdsTablesTabsEnum, sponsoredAdsRoutes } from "src/views/sponsored-ads/routes";

import { Box } from "@mui/material";
import { FilterAlertTypeEnum } from "src/components/Filters/FilterAlert/hooks/useFilterAlert.hooks";
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import { FORMAT_DATE, FORMAT_TIME, formatDateShortSlash } from "src/utils/formatters";
import { formatOfferDiscountItems } from "src/views/offer/utils/OfferListView.utils";

export const NOTIFICATIONS_LOGIC_TK = "NOTIFICATIONS_LOGIC_TK";

// TODO: rename the MarketingActionType enum in the api
const MarketingActionTypeMap = {
    offer: "offer",
    sponsoredAd: "sponsored-ad",
};

export const useGetNotificationText = (): ((
    notification: Notification
) => string | JSX.Element) => {
    const { t, translateCommon } = useTranslation(
        `${NOTIFICATIONS_TRANSLATION_NAMESPACE}:${NOTIFICATIONS_LOGIC_TK}`
    );
    const { t: translateRaw } = useTranslation();

    return function getNotificationText(notification: Notification): string | JSX.Element {
        const { key } = notification;
        const parameters = notification.parameters;
        const {
            service,
            channelName,
            supportName,
            offerSlotStartAt,
            items,
            itemsWithCategory,
            entity,
            marketingActionSlotStartAt,
        } = parameters;
        const offerSlotStartTime = moment(offerSlotStartAt || new Date()).format(FORMAT_TIME);
        const marketingActionSlotStartTime = moment(
            marketingActionSlotStartAt || new Date()
        ).format(FORMAT_TIME);
        const offerSlotStartDate = moment(offerSlotStartAt || new Date()).format(
            formatDateShortSlash()
        );
        const itemsString = (items || []).join(", ");
        const itemWithCategoryString = formatOfferDiscountItems(
            itemsWithCategory || [],
            translateRaw
        );

        const texts: Record<NotificationKey, () => string | JSX.Element> = {
            [NotificationKey.MealDealOfferNotPushed]: () => {
                return t("text.meal-deal-offer-not-pushed", {
                    channelName: translateCommon(`enums.channel-source.${channelName}`),
                    offerSlotStartTime,
                    offerSlotStartDate,
                });
            },
            [NotificationKey.CommissionNotFulfilled]: () => {
                return t(`text.${NotificationKey.CommissionNotFulfilled}`, {
                    channelName: translateCommon(`channel.${channelName}`),
                });
            },
            [NotificationKey.NewExternalPosChannel]: () => {
                return t(`text.${NotificationKey.NewExternalPosChannel}`);
            },
            [NotificationKey.OrganizationMissingCredential]: () => {
                return t("text.organization-missing-credential", { service: startCase(service) });
            },
            [NotificationKey.OrganizationInvalidCredential]: () => {
                return (
                    <>
                        <Box fontWeight="bold" display="inline">
                            {`${startCase(service)} :`}&nbsp;
                        </Box>
                        <span>{t("text.organization-invalid-credential")}</span>
                    </>
                );
            },
            [NotificationKey.PosMissingCredential]: () => {
                return t("text.pos-missing-credential", { service: startCase(service) });
            },
            [NotificationKey.PosInvalidCredential]: () => {
                return t("text.pos-invalid-credential", { service: startCase(service) });
            },
            [NotificationKey.PosMissingOpeningHours]: () => {
                return t("text.pos-missing-opening-hours");
            },
            [NotificationKey.PosOfferMissingProducts]: () => {
                return t("text.pos-offer-missing-products", {
                    itemsString: itemsString.length > 0 ? itemsString : itemWithCategoryString, // NEED TO KEEP ITEMS STRING TO STAY COMPATIBLE WITH OLD NOTIFICATIONS
                    service: startCase(service),
                });
            },
            [NotificationKey.PosOfferError]: () => {
                return t("text.pos-offer-error", {
                    channelName: translateCommon(`channel.${channelName}`),
                    offerSlotStartTime,
                    offerSlotStartDate,
                });
            },
            [NotificationKey.PosMarketingActionAlreadyOnline]: () => {
                return t("text.pos-marketing-action-already-online", {
                    service: startCase(service),
                    marketingActionSlotStartTime,
                    entity: translateCommon(`concepts.${MarketingActionTypeMap[entity]}`),
                });
            },
            [NotificationKey.PosOfferAlreadyOnline]: () => {
                return t("text.pos-offer-already-online", {
                    channelName: translateCommon(`channel.${channelName}`),
                    offerSlotStartTime: offerSlotStartTime,
                });
            },
            [NotificationKey.PosCredentialValidation]: () => {
                return t("text.pos-credential-validation", { service: startCase(service) });
            },
            [NotificationKey.PosMarketingActionNotQualified]: () => {
                return t(`text.${NotificationKey.PosMarketingActionNotQualified}`, {
                    supportName: startCase(supportName),
                    offerSlotStartTime: offerSlotStartTime,
                    offerSlotStartDate: offerSlotStartDate,
                });
            },
            [NotificationKey.PosOfferNotQualified]: () => {
                return t(`text.${NotificationKey.PosOfferNotQualified}`, {
                    channelName: startCase(translateCommon(`channel.${channelName}`)),
                    offerSlotStartTime: offerSlotStartTime,
                    offerSlotStartDate: offerSlotStartDate,
                });
            },
            [NotificationKey.PosMissingSepa]: () => {
                return t(`text.${NotificationKey.PosMissingSepa}`);
            },
            [NotificationKey.SpecialOfferNoLongerAvailable]: () => {
                return t("text.special-offer-no-longer-available", {
                    channelName: translateCommon(`channel.${channelName}`),
                    offerSlotStartTime,
                    offerSlotStartDate,
                });
            },
            [NotificationKey.ActionSlotNotCreated]: () => {
                if (entity === MarketingActionType.Offer) {
                    return t("text.offer-slot-not-created", {
                        channelName: translateCommon(`channel.${channelName}`),
                    });
                }
                return t("text.sponsored-ad-slot-not-created", {
                    service: startCase(service),
                });
            },
        };

        return texts[key]();
    };
};

export const notificationDateToDateString = (createdDateUTC: Date): string => {
    const { t } = useTranslation(
        `${NOTIFICATIONS_TRANSLATION_NAMESPACE}:${NOTIFICATIONS_LOGIC_TK}`
    );
    const date = moment(createdDateUTC);
    const isToday = date.isSame(moment(), "day");
    const isYesterday = date.isSame(moment().add(-1, "day"), "day");

    if (isToday) {
        return t("date.date-today", { date: date.format(FORMAT_TIME) });
    } else if (isYesterday) {
        return t("date.date-yesterday", { date: date.format(FORMAT_TIME) });
    }
    return t("date.date-global", { date: date.format(FORMAT_DATE) });
};

export const getNotificationLinkText = (notification: Notification): string => {
    const { t } = useTranslation(
        `${NOTIFICATIONS_TRANSLATION_NAMESPACE}:${NOTIFICATIONS_LOGIC_TK}`
    );
    const { key } = notification;
    const parameters = notification.parameters;

    const texts: Record<NotificationKey, () => string> = {
        [NotificationKey.CommissionNotFulfilled]: () =>
            t(`link.${NotificationKey.CommissionNotFulfilled}`),
        [NotificationKey.NewExternalPosChannel]: () =>
            t(`link.${NotificationKey.NewExternalPosChannel}`),
        [NotificationKey.OrganizationMissingCredential]: () =>
            t("link.organization-missing-credential"),
        [NotificationKey.OrganizationInvalidCredential]: () =>
            t("link.organization-invalid-credential"),
        [NotificationKey.PosMissingCredential]: () => t("link.pos-missing-credential"),
        [NotificationKey.PosInvalidCredential]: () => t("link.pos-invalid-credential"),
        [NotificationKey.PosMissingOpeningHours]: () => t("link.pos-missing-opening-hours"),
        [NotificationKey.PosOfferMissingProducts]: () => t("link.pos-offer-missing-products"),
        [NotificationKey.MealDealOfferNotPushed]: () => t("link.notification.agenda"),
        [NotificationKey.PosOfferError]: () => {
            const date = moment(parameters.offerSlotStartAt);
            const isToday = date.isSame(moment(), "day");

            return isToday ? t("link.notification.agenda") : t("link.notification.offers");
        },
        [NotificationKey.PosOfferAlreadyOnline]: () => {
            const date = moment(parameters.offerSlotStartAt);
            const isToday = date.isSame(moment(), "day");

            return isToday ? t("link.notification.agenda") : t("link.notification.offers");
        },
        [NotificationKey.PosMarketingActionAlreadyOnline]: () => {
            const date = moment(parameters.marketingActionSlotStartAt);
            const isToday = date.isSame(moment(), "day");

            return isToday ? t("link.notification.agenda") : t("link.notification.offers");
        },
        [NotificationKey.PosMarketingActionNotQualified]: () => {
            const date = moment(parameters.marketingActionSlotStartAt);
            const isToday = date.isSame(moment(), "day");

            return isToday ? t("link.notification.agenda") : t("link.notification.sponsored-ads");
        },
        [NotificationKey.PosOfferNotQualified]: () => {
            const date = moment(parameters.marketingActionSlotStartAt);
            const isTodayOrFuture = date.isSameOrAfter(moment(), "day");

            return isTodayOrFuture
                ? t("link.notification.agenda")
                : t("link.notification.sponsored-ads");
        },
        [NotificationKey.PosCredentialValidation]: () => {
            switch (parameters.service) {
                case ThirdServiceCategory.Deliveroo:
                    return t("link.pos-credential-validation.identity");
                case ThirdServiceCategory.Ubereats:
                    return t("link.pos-credential-validation.identity");
                default:
                    return t("link.pos-credential-validation.default");
            }
        },
        [NotificationKey.PosMissingSepa]: () => {
            return t(`link.${NotificationKey.PosMissingSepa}`);
        },
        [NotificationKey.SpecialOfferNoLongerAvailable]: () => {
            const date = moment(parameters.offerSlotStartAt);
            const isToday = date.isSame(moment(), "day");

            return isToday ? t("link.notification.agenda") : t("link.notification.offers");
        },
        [NotificationKey.ActionSlotNotCreated]: () => {
            const { entity } = parameters;

            return entity === "offer"
                ? t("link.notification.offers")
                : t("link.notification.sponsored-ads");
        },
    };

    return texts[key]();
};

export const getNotificationRedirection = (
    _history: History,
    notification: Notification
): string => {
    const { key } = notification;
    const parameters = notification.parameters;
    const { posId } = parameters;

    // http://localhost:3000/app/organization/external-pos
    const hrefs: Record<NotificationKey, () => string> = {
        [NotificationKey.CommissionNotFulfilled]: () => {
            return posRoutes.channelInfoView({ posId });
        },
        [NotificationKey.NewExternalPosChannel]: () => {
            return posRoutes.channelMappingView({ posId });
        },
        [NotificationKey.OrganizationMissingCredential]: () => {
            switch (parameters.service) {
                case ThirdServiceCategory.Belorder:
                    return organizationRoutes.externalToolsView({
                        tab: OrganizationToolsTabsEnum.SalesChannel,
                        service: parameters.service,
                    });
                default:
                    return organizationRoutes.externalToolsView({
                        tab: OrganizationToolsTabsEnum.ExternalPos,
                        service: parameters.service,
                    });
            }
        },
        [NotificationKey.OrganizationInvalidCredential]: () => {
            switch (parameters.service) {
                case ThirdServiceCategory.Belorder:
                    return organizationRoutes.externalToolsView({
                        tab: OrganizationToolsTabsEnum.SalesChannel,
                        service: parameters.service,
                    });
                default:
                    return organizationRoutes.externalToolsView({
                        tab: OrganizationToolsTabsEnum.ExternalPos,
                        service: parameters.service,
                    });
            }
        },
        [NotificationKey.PosMissingCredential]: () => {
            return posRoutes.platformConnectionView({ posId });
        },
        [NotificationKey.PosInvalidCredential]: () => {
            return posRoutes.platformConnectionView({ posId });
        },
        [NotificationKey.PosMissingOpeningHours]: () => {
            return posRoutes.detailView({ posId });
        },
        [NotificationKey.PosOfferMissingProducts]: () => {
            return offersRoutes.tablesView({
                tab: OffersTablesTabsEnum.MANAGEMENT,
                offerIds: parameters.offerId ? [parameters.offerId] : undefined,
                filterAlert: FilterAlertTypeEnum.OfferNotification,
            });
        },
        [NotificationKey.MealDealOfferNotPushed]: () => {
            return agendaRoutes.tablesView({
                tab: AgendaListTabsEnum.OFFER,
                slotIds: parameters.offerSlotId ? [parameters.offerSlotId] : undefined,
                filterAlert: FilterAlertTypeEnum.OfferNotification,
            });
        },
        [NotificationKey.PosOfferError]: () => {
            const date = moment(parameters.offerSlotStartAt);
            const isToday = date.isSame(moment(), "day");
            return isToday
                ? agendaRoutes.tablesView({
                      tab: AgendaListTabsEnum.OFFER,
                      slotIds: parameters.offerSlotId ? [parameters.offerSlotId] : undefined,
                      filterAlert: FilterAlertTypeEnum.OfferNotification,
                  })
                : offersRoutes.tablesView({
                      tab: OffersTablesTabsEnum.MANAGEMENT,
                      offerIds: parameters.offerId ? [parameters.offerId] : undefined,
                      filterAlert: FilterAlertTypeEnum.OfferNotification,
                  });
        },
        [NotificationKey.PosOfferAlreadyOnline]: () => {
            const date = moment(parameters.offerSlotStartAt);
            const isToday = date.isSame(moment(), "day");

            return isToday
                ? agendaRoutes.tablesView({
                      tab: AgendaListTabsEnum.OFFER,
                      slotIds: parameters.offerSlotId ? [parameters.offerSlotId] : undefined,
                      filterAlert: FilterAlertTypeEnum.OfferNotification,
                  })
                : offersRoutes.tablesView({
                      tab: OffersTablesTabsEnum.MANAGEMENT,
                      offerIds: parameters.offerId ? [parameters.offerId] : undefined,
                      filterAlert: FilterAlertTypeEnum.OfferNotification,
                  });
        },
        [NotificationKey.PosMarketingActionAlreadyOnline]: () => {
            const date = moment(parameters.marketingActionSlotStartAt);
            const isToday = date.isSame(moment(), "day");

            return isToday
                ? agendaRoutes.tablesView({
                      tab: AgendaListTabsEnum.SPONSORED_AD,
                      slotIds: parameters.marketingActionSlotId
                          ? [parameters.marketingActionSlotId]
                          : undefined,
                      filterAlert: FilterAlertTypeEnum.SponsoredAdNotification,
                  })
                : sponsoredAdsRoutes.tablesView({
                      tab: SponsoredAdsTablesTabsEnum.MANAGEMENT,
                      sponsoredAdIds: parameters.marketingActionId
                          ? [parameters.marketingActionId]
                          : undefined,
                      filterAlert: FilterAlertTypeEnum.SponsoredAdNotification,
                  });
        },
        [NotificationKey.PosMarketingActionNotQualified]: () => {
            const date = moment(parameters.marketingActionSlotStartAt);
            const isToday = date.isSame(moment(), "day");

            return isToday
                ? agendaRoutes.tablesView({
                      tab: AgendaListTabsEnum.SPONSORED_AD,
                      slotIds: parameters.marketingActionSlotId
                          ? [parameters.marketingActionSlotId]
                          : undefined,
                      filterAlert: FilterAlertTypeEnum.SponsoredAdNotification,
                  })
                : sponsoredAdsRoutes.tablesView({
                      tab: SponsoredAdsTablesTabsEnum.MANAGEMENT,
                      sponsoredAdIds: parameters.marketingActionId
                          ? [parameters.marketingActionId]
                          : undefined,
                      filterAlert: FilterAlertTypeEnum.SponsoredAdNotification,
                  });
        },
        [NotificationKey.PosOfferNotQualified]: () => {
            const date = moment(parameters.marketingActionSlotStartAt);
            const isTodayOrFuture = date.isSameOrAfter(moment(), "day");

            return isTodayOrFuture
                ? agendaRoutes.tablesView({
                      tab: AgendaListTabsEnum.OFFER,
                      slotIds: parameters.marketingActionSlotId
                          ? [parameters.marketingActionSlotId]
                          : undefined,
                      filterAlert: FilterAlertTypeEnum.OfferNotification,
                  })
                : offersRoutes.tablesView({
                      tab: OffersTablesTabsEnum.MANAGEMENT,
                      offerIds: parameters.marketingActionId
                          ? [parameters.marketingActionId]
                          : undefined,
                      filterAlert: FilterAlertTypeEnum.OfferNotification,
                  });
        },
        [NotificationKey.PosCredentialValidation]: () => {
            switch (parameters.service) {
                case ThirdServiceCategory.Deliveroo:
                case ThirdServiceCategory.Ubereats:
                    return posRoutes.platformConnectionView({ posId });
                default:
                    return posRoutes.channelMappingView({ posId });
            }
        },
        [NotificationKey.PosMissingSepa]: () => organizationRoutes.detailsView(),
        [NotificationKey.SpecialOfferNoLongerAvailable]: () => {
            return offersRoutes.tablesView({
                tab: OffersTablesTabsEnum.MANAGEMENT,
                offerIds: parameters.offerId ? [parameters.offerId] : undefined,
                filterAlert: FilterAlertTypeEnum.OfferNotification,
            });
        },
        [NotificationKey.ActionSlotNotCreated]: () => {
            if (parameters.entity === "offer") {
                return offersRoutes.tablesView({
                    tab: OffersTablesTabsEnum.MANAGEMENT,
                    offerIds: parameters.marketingActionId
                        ? [parameters.marketingActionId]
                        : undefined,
                    filterAlert: FilterAlertTypeEnum.OfferNotification,
                });
            }
            return sponsoredAdsRoutes.tablesView({
                tab: SponsoredAdsTablesTabsEnum.MANAGEMENT,
                sponsoredAdIds: parameters.marketingActionId
                    ? [parameters.marketingActionId]
                    : undefined,
                filterAlert: FilterAlertTypeEnum.OfferNotification,
            });
        },
    };

    return hrefs[key]();
};
